<script setup lang="tsx">
import { ref, watch, onMounted, nextTick, computed, type WatchStopHandle } from 'vue'
import http from '@/services/http'
import OrderLabel from '@/components/widgets/OrderLabel.vue'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import useVulnerabilityColumns from './vulnerabilityColumns'
import columnStore from '@/stores/column'
import type { PageRequest, PageResponse, Response, SaveVulnerabilitiesRequest, VulnerabilityMeta } from '@/models/api-generated'
import LoadingContainer from '@/components/widgets/LoadingContainer.vue'
import { refreshVulnerabilityCallback } from './vulnerabilityShared'
import type { VulnerabilityFilter } from '@/models/filters-generated'
import PlusButton from '@/components/widgets/PlusButton.vue'
import IconButton from '@/components/shared/IconButton.vue'
import sliderStore from '@/stores/slider'
import { useI18n } from 'vue-i18n'
import VulnerabilityFilters from './VulnerabilityFilters.vue'
import toastStore from '@/stores/toast'
import { VulnerabilityReference } from '@/models/enums-generated'
import { cloneDeep, isEqual } from 'lodash'
import RiskScoreBadge from '../RiskScoreBadge.vue'
import RiskCategorySelect from '../matrix/RiskCategorySelect.vue'
import RiskCatalogSelect from '../matrix/RiskCatalogSelect.vue'
import CommentReferencesContainer from '@/components/comment_references/CommentReferencesContainer.vue'
import { h } from 'vue'
import RisksTooltip from '../RisksTooltip.vue'
import { newDefaultCatalog } from '../shared'
import { defaultInstance } from '@/services/utils'

const props = defineProps({
	component: { default: 'vulnerabilities' },
})

const toaster = toastStore()
const model = ref<VulnerabilityMeta[]>([])
const totalPages = ref<number>()
const isAllExpanded = ref(false)
const currentPage = ref(1)
const limit = ref(50)
const isLoading = ref(false)
const { columns } = useVulnerabilityColumns(props.component)
const slider = sliderStore()
const { t } = useI18n()
const changed = ref(new Set<number>())
const deleted = ref(new Set<number>())
const isSaving = ref(false)
let originalModel: VulnerabilityMeta[] = []
const watches: WatchStopHandle[] = []
const isEdit = ref(false)

const root = columnStore().defineComponent(props.component, fetch)
refreshVulnerabilityCallback.value = fetch

function defaultVulnerability(): VulnerabilityMeta {
	return {
		vulnerability: {
			id: 0,
			code: '',
			name: '',
			isCustom: true,
			description: '',
		},

		id: 0,
		score: 0,
		instance: defaultInstance(),
		comment: '',
		urlReferences: [],
		documentReferences: [],
		history: {},
		isExpanded: true,
		risks: [],
		catalogMeta: newDefaultCatalog(),
		category: { id: 0, isCustom: true, name: '', metaId: 0, instanceId: 0 },
	}
}

watch(isAllExpanded, (value) => {
	nextTick(() => {
		for (let a of model.value ?? []) {
			a.isExpanded = value
		}
	})
})

function vulnerabilityDataForComparison(row: VulnerabilityMeta) {
	return {
		id: row.id,
		vulnerability: row.vulnerability,
		comment: row.comment,
		urlReference: row.urlReferences,
		documentReference: row.documentReferences,
		history: row.history,
		instance: row.instance,
		catalog: row.catalogMeta,
		category: row.category,
	}
}

watch(
	model,
	(newVal) => {
		changed.value.clear()
		newVal.forEach((row) => {
			if (row.id === 0 || deleted.value.has(row.id)) return
			const originalRow = originalModel.find((r) => r.id === row.id)
			if (originalRow && !isEqual(vulnerabilityDataForComparison(row), vulnerabilityDataForComparison(originalRow))) {
				changed.value.add(row.id)
			}
		})
	},
	{ deep: true },
)

onMounted(async () => {
	slider.watchChanges(model)
})

const numChanges = computed(() => changed.value.size + deleted.value.size + model.value.filter((x) => x.id === 0).length)
const validVulnerabilities = (rows: VulnerabilityMeta[]) =>
	rows.every((r) => r.vulnerability.code.length && r.vulnerability.name.length && r.vulnerability.description.length && r.catalogMeta.id)

const canSave = computed(() => validVulnerabilities(model.value) && numChanges.value > 0)

function rowClass(i: number) {
	return {
		'is-changed': !model.value[i].id || (changed.value.has(model.value[i].id) && !deleted.value.has(model.value[i].id)),
		'is-deleted': deleted.value.has(model.value[i].id),
	}
}

async function fetch(signal: AbortSignal) {
	watches.forEach((x) => x())
	watches.splice(0, watches.length)
	changed.value.clear()
	const request: PageRequest<VulnerabilityFilter> = {
		page: currentPage.value,
		limit: limit.value,
		filter: root.filterValue() as VulnerabilityFilter,
		orderBy: root.orderValue(),
	}
	isLoading.value = true
	const { data } = await http.post<Response<PageResponse<VulnerabilityMeta>>>(`api/vulnerability/list`, request, { signal })
	isLoading.value = false
	if (!data.data) return

	model.value = data.data.items
	originalModel = cloneDeep(model.value)

	totalPages.value = data.data.pages
}

async function save() {
	if (!model.value) return

	if (deleted.value.size > 0) {
		return slider.requestDeleteConfirm(t('misc.confirmDelete'), 'vulnerability', async () => {
			await doSave()
		})
	}

	await doSave()
}

async function doSave() {
	if (!model.value) return
	isLoading.value = true
	isSaving.value = true
	const request: SaveVulnerabilitiesRequest = {
		added: model.value.filter((x) => x.id === 0),
		changed: model.value.filter((x) => changed.value.has(x.id) && !deleted.value.has(x.id)),
		deleted: Array.from(deleted.value),
	}
	await http.post(`api/vulnerability/save`, request)
	changed.value.clear()
	deleted.value.clear()
	root.triggerFetch()

	isLoading.value = false
	isSaving.value = false
	isEdit.value = false
	toaster.showSuccess(t('misc.successfullyEdited'))
}

function addVulnerability() {
	if (!model.value) return
	isEdit.value = true
	model.value.unshift(defaultVulnerability())
}

function editVulnerability() {
	isEdit.value = !isEdit.value
}

async function reset() {
	isEdit.value = false
	model.value = []
	changed.value.clear()
	deleted.value.clear()
	root.triggerFetch()
}

function remove(index: number) {
	if (!model.value) return
	deleted.value.add(model.value[index].id)
}

function toggleExpand(i: number, e: MouseEvent) {
	const target = e.target as HTMLElement
	if (target.closest('input, select, textarea, button, .bi-trash, .method-expand-icon, .multiselect')) return
	model.value[i].isExpanded = !model.value[i].isExpanded
}

const emit = defineEmits<{
	(e: 'go-to-risks', riskNames: string[], treatments: string[]): void
}>()

function goToRisks(names: string[]) {
	emit('go-to-risks', names, [])
}

defineExpose({
	save,
	reset,
	addVulnerability,
	editVulnerability,
	numChanges,
	canSave,
	isSaving,
	isEdit,
})
</script>

<template>
	<span v-show="!isEdit"><VulnerabilityFilters v-show="!isEdit" :component="props.component"></VulnerabilityFilters></span>

	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3">
		<div class="table-responsive w-100">
			<table class="table">
				<thead>
					<tr>
						<th class="w-auto-32"><PlusButton v-model="isAllExpanded" /></th>
						<th v-if="columns.code.value.isVisible">
							<OrderLabel v-model="columns.code.value">{{ columns.code.value.label }}</OrderLabel>
						</th>
						<th v-if="columns.name.value.isVisible">
							<OrderLabel v-model="columns.name.value">{{ columns.name.value.label }}</OrderLabel>
						</th>
						<th v-if="columns.catalog.value.isVisible">
							<OrderLabel v-model="columns.catalog.value">{{ columns.catalog.value.label }}</OrderLabel>
						</th>
						<th v-if="columns.category.value.isVisible">
							<OrderLabel v-model="columns.category.value">{{ columns.category.value.label }}</OrderLabel>
						</th>
						<th v-if="columns.connectedRisks.value.isVisible">
							{{ columns.connectedRisks.value.label }}
						</th>
						<th v-if="columns.score.value.isVisible">
							<OrderLabel v-model="columns.score.value">{{ columns.score.value.label }}</OrderLabel>
						</th>
						<th v-if="isEdit"></th>
					</tr>
				</thead>
				<tbody>
					<template v-for="(meta, i) in model" :key="meta.id">
						<tr class="vulnerability p-2" :class="[rowClass(i), i % 2 === 0 ? 'odd' : 'even']" @click="toggleExpand(i, $event)">
							<td><PlusButton v-model="meta.isExpanded" /></td>

							<td v-if="columns.code.value.isVisible">
								<input
									v-if="isEdit && meta.vulnerability.isCustom"
									v-model="meta.vulnerability.code"
									:data-test="'vulnerability-code-input-' + meta.id"
									:class="{ 'is-invalid': !meta.vulnerability.code }"
									class="form-control form-control-sm"
								/>
								<span v-else>{{ meta.vulnerability.code }}</span>
							</td>

							<td v-if="columns.name.value.isVisible">
								<input
									v-if="isEdit && meta.vulnerability.isCustom"
									v-model="meta.vulnerability.name"
									:data-test="'vulnerability-name-input-' + meta.id"
									:class="{ 'is-invalid': !meta.vulnerability.name }"
									class="form-control form-control-sm"
								/>
								<span v-else>{{ meta.vulnerability.name }}</span>
							</td>

							<td v-if="columns.catalog.value.isVisible">
								<RiskCatalogSelect
									v-if="isEdit"
									:multi-select="false"
									v-model="meta.catalogMeta"
									:data-test="'vulnerability-catalog-input-' + meta.id"
									:class="{ 'is-invalid': !meta.catalogMeta.id }"
									:allow-empty="false"
								/>
								<span v-else>{{ meta.catalogMeta.catalog.name }}</span>
							</td>

							<td v-if="columns.category.value.isVisible">
								<RiskCategorySelect
									v-if="isEdit"
									v-model="meta.category"
									:exclude-ids="meta.category?.id ? [meta.category.id] : []"
									:data-test="'vulnerability-category-input-' + meta.id"
								/>
								<span v-else>{{ meta.category.name }}</span>
							</td>

							<td
								v-if="columns.connectedRisks.value.isVisible"
								v-one-tooltip="{
									content: () => h(RisksTooltip, { risks: meta.risks }),
								}"
								@click.stop="goToRisks(meta.risks.map((r) => r.name))"
								style="cursor: pointer"
							>
								{{ meta.risks.length }}
							</td>

							<td v-if="columns.score.value.isVisible">
								<RiskScoreBadge v-model="meta.score"></RiskScoreBadge>
							</td>

							<template v-if="isEdit">
								<td v-if="meta.vulnerability.isCustom" class="text-end">
									<IconButton
										:data-test="'vulnerability-trash-' + meta.id"
										class="bi bi-trash"
										:title="$t('misc.delete')"
										@click="remove(i)"
									/>
								</td>
								<td v-else></td>
							</template>
						</tr>
						<tr v-if="meta.isExpanded">
							<td colspan="100%">
								<CommentReferencesContainer
									v-if="model"
									v-model="model[i].comment"
									:canEdit="isEdit"
									:url-references="model[i].urlReferences"
									:document-references="model[i].documentReferences"
									:owner-id="model[i].id"
									:owner-type="VulnerabilityReference"
								></CommentReferencesContainer>
							</td>
						</tr>
						<tr v-if="meta.isExpanded">
							<td colspan="100%" class="pt-4">
								<div class="card">
									<div class="card-body">
										<h6 class="card-title text-center fw-bold">{{ $t('vulnerability.description') }}</h6>
										<div>
											<template v-if="model">
												<textarea
													v-if="isEdit && meta.vulnerability.isCustom"
													v-model="meta.vulnerability.description"
													:data-test="'vulnerability-description-input-' + meta.id"
													:class="{ 'is-invalid': !meta.vulnerability.description }"
													class="form-control mb-2"
													rows="3"
													maxlength="500"
												></textarea>
												<span v-else>{{ meta.vulnerability.description }}</span></template
											>
											<span v-else>-</span>
										</div>
									</div>
								</div>
							</td>
						</tr>
					</template>
				</tbody>
			</table>
		</div>
	</LoadingContainer>

	<Pagination v-show="!isEdit" v-model="root.pagination" :total-pages="totalPages" tableTitle="vulnerabilities" />
</template>
